import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Menu, 
  MenuItem, 
  IconButton, 
  Typography, 
  Box,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useLanguage } from '../contexts/LanguageContext';

const LanguageSwitcherV2 = ({ variant = 'button', size = 'medium', buttonColor = 'inherit' }) => {
  const { language, changeLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  
  // List of supported languages
  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'es', label: 'Español', flag: '🇪🇸' }
  ];

  // Update current language when context language changes
  useEffect(() => {
    setCurrentLanguage(language);
  }, [language]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [isChanging, setIsChanging] = useState(false);
  
  const handleLanguageChange = (langCode) => {
    if (langCode === currentLanguage) {
      handleClose();
      return;
    }
    
    setIsChanging(true);
    console.log(`Switching language to: ${langCode}`);
    
    try {
      // Save language in localStorage
      localStorage.setItem('language', langCode);
      
      // Update context language state directly
      changeLanguage(langCode);
      
      // Update component local state
      setCurrentLanguage(langCode);
      
      // Close the menu
      handleClose();
      
      // Dispatch a custom event for any component that needs to know about language changes
      const event = new CustomEvent('languageChanged', { detail: { language: langCode } });
      window.dispatchEvent(event);
      
      // Short delay before allowing another change
      setTimeout(() => {
        setIsChanging(false);
      }, 300);
      
    } catch (error) {
      console.error('Error changing language:', error);
      setIsChanging(false);
    }
  };

  // Get current language label
  const getCurrentLanguageLabel = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? lang.label : 'English';
  };

  // Get current language flag
  const getCurrentLanguageFlag = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? lang.flag : '🇺🇸';
  };

  return (
    <div>
      {variant === 'button' ? (
        <Button
          color={buttonColor}
          startIcon={isChanging ? <CircularProgress size={16} color="inherit" /> : <LanguageIcon />}
          endIcon={!isChanging && <KeyboardArrowDownIcon />}
          onClick={handleClick}
          size={size}
          aria-controls="language-menu"
          aria-haspopup="true"
          disabled={isChanging}
        >
          {getCurrentLanguageFlag()} {getCurrentLanguageLabel()}
        </Button>
      ) : (
        <IconButton 
          color={buttonColor} 
          onClick={handleClick}
          size={size}
          aria-controls="language-menu"
          aria-haspopup="true"
          disabled={isChanging}
        >
          {isChanging ? <CircularProgress size={16} color="inherit" /> : <LanguageIcon />}
        </IconButton>
      )}
      
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {languages.map((lang) => (
          <MenuItem 
            key={lang.code} 
            onClick={() => handleLanguageChange(lang.code)}
            selected={currentLanguage === lang.code}
            sx={{ 
              minWidth: 140,
              py: 1
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Typography variant="body2" fontSize="1.2rem">{lang.flag}</Typography>
            </ListItemIcon>
            <ListItemText primary={lang.label} />
            {currentLanguage === lang.code && (
              <CheckIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default LanguageSwitcherV2;
